/**
 * Universal image / video normalisation pipeline for the volunteer registration form.
 *
 * Why this exists:
 *  - iPhones write HEIC/HEIF photos by default (iOS 11+) and HEVC-encoded MOV videos.
 *    Neither browsers nor the iElect KYC service handle HEIC reliably.
 *  - Android galleries can return WebP / AVIF files.
 *  - Photos taken in landscape on iPhones often store orientation in EXIF rather
 *    than rotating the pixels, which causes selfies to upload sideways and fail
 *    the 90% liveness threshold downstream.
 *  - Stock JPEGs from modern phones routinely exceed the 5MB cap.
 *
 * What we do:
 *  - Detect HEIC / HEIF and convert to JPEG via `heic2any` (lazy-loaded).
 *  - Run every image through `browser-image-compression` (also lazy-loaded) to
 *    bake the EXIF rotation into pixels, downscale to 1920px on the long edge,
 *    and emit a clean JPEG ≤ 4 MB.
 *  - For videos: infer the MIME from the extension when the browser hands us
 *    `application/octet-stream`, surface a `warnHEVC` flag so the UI can nudge
 *    the user toward a still photo. We do NOT transcode video.
 *
 * Both `heic2any` and `browser-image-compression` are dynamic-imported so the
 * libheif WASM (~200 KB) only ships when the user actually picks a HEIC file
 * and the compression worker only loads on first image upload. See
 * `vite.config.ts` `manualChunks: { 'media-conv': [...] }`.
 */

const HEIC_MIME = ['image/heic', 'image/heif'];

const EXT_MIME: Record<string, string> = {
	'heic': 'image/heic',
	'heif': 'image/heif',
	'webp': 'image/webp',
	'avif': 'image/avif',
	'jpg': 'image/jpeg',
	'jpeg': 'image/jpeg',
	'png': 'image/png',
	'gif': 'image/gif',
	'mp4': 'video/mp4',
	'mov': 'video/quicktime',
	'webm': 'video/webm',
	'avi': 'video/x-msvideo',
	'3gp': 'video/3gpp',
	'3gpp': 'video/3gpp',
};

const getExtension = (name: string): string => {
	const dot = name.lastIndexOf('.');
	return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const replaceExtension = (name: string, newExt: string): string => {
	const dot = name.lastIndexOf('.');
	const stem = dot >= 0 ? name.slice(0, dot) : name;
	return `${stem}.${newExt}`;
};

/**
 * Best-effort MIME inference from filename extension. Used when the browser
 * delivers a File with an empty `type` (common for HEIC paths on some iOS
 * Safari versions and for files dropped from outside the page).
 */
export const inferMimeFromExtension = (name: string): string | null => {
	const ext = getExtension(name);
	return EXT_MIME[ext] ?? null;
};

const isHeic = (file: File): boolean => {
	if (HEIC_MIME.includes(file.type)) return true;
	if (!file.type) {
		const ext = getExtension(file.name);
		return ext === 'heic' || ext === 'heif';
	}
	return false;
};

const heic2anyOnce = async (): Promise<typeof import('heic2any').default> => {
	const mod = await import('heic2any');
	return mod.default;
};

const compressorOnce = async () => {
	const mod = await import('browser-image-compression');
	return mod.default;
};

const heicToJpeg = async (file: File): Promise<File> => {
	const heic2any = await heic2anyOnce();
	const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
	const single = Array.isArray(blob) ? blob[0] : blob;
	return new File([single], replaceExtension(file.name || 'photo.jpg', 'jpg'), { type: 'image/jpeg' });
};

const compressImage = async (file: File): Promise<File> => {
	const compressor = await compressorOnce();
	const compressed = await compressor(file, {
		maxSizeMB: 4,
		maxWidthOrHeight: 1920,
		useWebWorker: true,
		fileType: 'image/jpeg',
		// browser-image-compression defaults already apply EXIF rotation via canvas.
	});
	const out = compressed instanceof File ? compressed : new File([compressed], file.name, { type: 'image/jpeg' });
	// Ensure name ends in .jpg for downstream MIME consistency.
	if (out.type === 'image/jpeg' && !/\.jpe?g$/i.test(out.name)) {
		return new File([out], replaceExtension(out.name || 'photo.jpg', 'jpg'), { type: 'image/jpeg' });
	}
	return out;
};

/**
 * Selfie / voter-card image pipeline:
 *   HEIC → JPEG (heic2any) → compress + EXIF-rotate (browser-image-compression)
 *   non-HEIC → compress + EXIF-rotate
 * Always returns a JPEG ≤ 4 MB, ≤ 1920 px long edge.
 */
export const normaliseImage = async (file: File): Promise<File> => {
	const stage1 = isHeic(file) ? await heicToJpeg(file) : file;
	return compressImage(stage1);
};

export const normaliseSelfieImage = normaliseImage;
export const normaliseCardImage = normaliseImage;

export interface NormaliseVideoResult {
	file: File;
	warnHEVC: boolean;
	sizeError: string | null;
}

const HEVC_HINT_MIME = new Set(['video/quicktime', 'video/x-quicktime', 'video/x-m4v']);
const MAX_VIDEO_BYTES = 30 * 1024 * 1024;

/**
 * Selfie video pipeline:
 *  - Repair MIME if browser handed us empty `type` (avoids application/octet-stream
 *    failures at media-manager / KYC).
 *  - Heuristically flag HEVC so the UI can show the upfront "prefer photo" nudge.
 *  - Enforce the 30 MB cap.
 *  - Does NOT transcode (would need ffmpeg.wasm at ≥10 MB).
 */
export const normaliseSelfieVideo = (file: File): NormaliseVideoResult => {
	const ext = getExtension(file.name);
	const inferredMime = file.type || EXT_MIME[ext] || '';
	const repaired =
		file.type === inferredMime
			? file
			: new File([file], file.name || `selfie.${ext || 'mp4'}`, { type: inferredMime || 'video/mp4' });

	// HEVC is most commonly the codec inside .mov from iPhone Camera, but we
	// can't read codec from MIME alone — best heuristic is .mov + quicktime MIME.
	const warnHEVC = ext === 'mov' || HEVC_HINT_MIME.has(repaired.type);

	const sizeError =
		repaired.size > MAX_VIDEO_BYTES
			? `Video is ${(repaired.size / (1024 * 1024)).toFixed(1)} MB. Please use a clip under 30 MB.`
			: null;

	return { file: repaired, warnHEVC, sizeError };
};
